import type { ComplianceLessonKey } from './types'

export const INTAKE_API_PATH = '/finances/api/v2/intake'
export const REPORT_API_PATH = '/finances/api/v2/compliance/report'

/**
 * Every route in the compliance funnel, in one place — the route table, the
 * app shell's focused-nav check, and the in-funnel links all read from here,
 * so a rename stays a one-file change (the same rule `SIGNUP_PATHS` follows
 * for the signup routes).
 */
export const COMPLIANCE_PATHS = {
  intake: '/practice-readiness/intake',
  report: '/practice-readiness/report',
  planPricing: '/practice-readiness/plans',
} as const

/**
 * The funnel's page width. Sized to the report, the widest thing the funnel
 * renders, so the chrome never crops it.
 *
 * Exported on its own because the intake's fixed footer needs the width without
 * the centring and padding `FUNNEL_PAGE_CLS` carries, and the bar has to keep
 * agreeing with the progress bar above it.
 */
export const FUNNEL_MAX_W_CLS = 'max-w-5xl'

/**
 * The funnel's page container — what the header, the intake's progress bar, and
 * the intake's footer buttons all line up against.
 *
 * The screens keep their narrower content columns centred inside this, the way
 * the signup flow's content sits narrower than its progress bar: chrome that
 * holds still while content changes width is the point, rather than chrome that
 * moves screen to screen.
 */
export const FUNNEL_PAGE_CLS = `mx-auto w-full ${FUNNEL_MAX_W_CLS} px-6`

const COMPLIANCE_PATH_LIST: readonly string[] = Object.values(COMPLIANCE_PATHS)

/** Whether a pathname is one of the funnel's own routes — exact matches only,
 *  so an unrelated route that happens to share a prefix never counts. */
export const isCompliancePath = (pathname: string): boolean =>
  COMPLIANCE_PATH_LIST.includes(pathname)

export const FETCH_INTAKE_KEY = 'FETCH_COMPLIANCE_INTAKE'
export const FETCH_REPORT_KEY = 'FETCH_COMPLIANCE_REPORT'

/**
 * Display order for the intake flow. `money_books` (a single question,
 * `money_system`) is deliberately absent — it now renders inside
 * `getting_paid` ("Your money") instead of as its own section. `money_books`
 * stays in `ComplianceLessonKey` (and the exhaustive Records below) purely
 * so those types keep tracking the backend contract; it's otherwise unused.
 */
export const SECTION_ORDER: readonly ComplianceLessonKey[] = [
  'practice_stage',
  'entity',
  'license',
  'insurance',
  'getting_paid',
]

/**
 * The `tax_treatment` answer vocabulary — the contract with the API's question schema.
 *
 * Shared rather than redeclared: a rename that misses a consumer fails silently, and one
 * of those consumers is the qualification verdict, which fails open.
 */
export const TAX_TREATMENTS = {
  soleprop: 'soleprop',
  scorp: 'scorp',
  ccorp: 'ccorp',
  other: 'other',
} as const

export type TaxTreatment = keyof typeof TAX_TREATMENTS

const TAX_TREATMENT_VALUES: ReadonlySet<string> = new Set(
  Object.values(TAX_TREATMENTS)
)

/** Narrows an untyped intake answer, which is `unknown` off the wire. */
export const isTaxTreatment = (value: unknown): value is TaxTreatment =>
  typeof value === 'string' && TAX_TREATMENT_VALUES.has(value)

/**
 * The `has_entity` answer vocabulary. Must stay identical to the `has_entity`
 * enum in the API's `compliance/question_schemas.ts`: the two apps share no
 * module, so a change here has to be mirrored by hand or the API rejects the
 * answer.
 */
export const HAS_ENTITY = {
  yes: 'yes',
  no: 'no',
  notsure: 'notsure',
} as const

/**
 * What a skippable date question stores when the user says "I don't know".
 *
 * `null` already means "never asked, or cleared", so a skip written as `null`
 * reaches the report as an absence and every assessor falls through to a
 * default. Must stay identical to `UNKNOWN_ANSWER` in the API's
 * `compliance/constants.ts` — the two apps share no module, so a change here
 * has to be mirrored by hand or the API rejects the answer.
 */
export const UNKNOWN_ANSWER = 'unknown'

export const isUnknownAnswer = (value: unknown): boolean =>
  value === UNKNOWN_ANSWER

/**
 * What `entity_formed_at` stores when the user says they have no legal entity.
 * Must stay identical to `NOT_APPLICABLE_ANSWER` in the API's
 * `compliance/constants.ts`: the two apps share no module, so a change here has
 * to be mirrored by hand or the API rejects the answer.
 */
export const NOT_APPLICABLE_ANSWER = 'not_applicable'

export const isNotApplicableAnswer = (value: unknown): boolean =>
  value === NOT_APPLICABLE_ANSWER

/** Either sentinel. Anything asking "is this a real date?" wants this, not one
 *  of the two: both are non-empty strings that survive a falsy guard. */
export const isSentinelAnswer = (value: unknown): boolean =>
  isUnknownAnswer(value) || isNotApplicableAnswer(value)

/**
 * The `expected_income_band` answer vocabulary and its display labels — the contract
 * with the API's question schema.
 */
export const INCOME_BANDS = {
  u20: 'Under $20K',
  '20-40': '$20–40K',
  '40-80': '$40–80K',
  '80-150': '$80–150K',
  '150+': 'Over $150K',
} as const

/**
 * The pre-split band. Still accepted by the API and still labelled here so a stored
 * answer renders as text rather than its raw code, but no slider position produces it.
 */
export const LEGACY_INCOME_BAND = { u40: 'Under $40K' } as const

/**
 * Bands at which quarterly estimated taxes apply. Must stay identical to
 * `EARNING_BANDS` in the API's `obligations/practice_stage.ts` — the two apps share no
 * module, so a change in one has to be mirrored by hand or the intake summary card and
 * the report contradict each other.
 *
 * An allow-list rather than an exclusion of the lowest band, deliberately, and the
 * same polarity the report applies. Excluding the bottom instead would mean any band
 * added later defaults to claiming the obligation — a card asserting quarterly taxes
 * apply while the report calls them not relevant, arriving through the floor.
 */
export const QUARTERLY_TAX_INCOME_BANDS: ReadonlySet<string> = new Set([
  '20-40',
  '40-80',
  '80-150',
  '150+',
])

export const SECTION_LABELS: Record<ComplianceLessonKey, string> = {
  license: 'Your license',
  practice_stage: 'Your practice',
  entity: 'Your business & tax entity',
  insurance: 'Your protection',
  getting_paid: 'Your money',
  money_books: 'Money & books',
}

/** Short segment labels for the horizontal progress bar (full labels are too wide at 6-across). */
export const SECTION_SHORT_LABELS: Record<ComplianceLessonKey, string> = {
  license: 'License',
  practice_stage: 'Practice',
  entity: 'Entity',
  insurance: 'Protection',
  getting_paid: 'Money',
  money_books: 'Money',
}

export const SECTION_INTROS: Record<ComplianceLessonKey, string> = {
  license:
    'Tell us about your license, your credentials, and the dates attached to them. Renewal cycles and continuing education rules are set state by state, so this is what tells us which ones apply to you.',
  practice_stage:
    'Tell us more about where your practice is today, so we only surface what actually applies to you right now.',
  entity:
    'How your practice is set up and taxed. This drives entity filings, payroll, and what the IRS expects.',
  insurance:
    'Tell us about the coverage you have that keeps a bad day from becoming a bad year.',
  getting_paid: 'Tell us how money reaches you and how you keep it organized.',
  money_books: 'How you keep the books today, so we know where to meet you.',
}

/** Re-exported so the feature has one import for state naming; the list itself
 *  lives with the other location constants. */
export { stateNameForCode as stateName } from '@/constants/locationConstants'

/**
 * The states a practice is licensed in, as codes. Licenses win when present, since
 * the report's jurisdictions are only a fallback for a report composed before any
 * license was reconciled. `federal` is a jurisdiction the composer emits, not a
 * place anyone holds a license, so it never belongs in this list.
 */
export const licensedStateCodes = (
  licenses: { state: string }[],
  jurisdictions: string[] = []
): string[] => [
  ...new Set(
    (licenses.length ? licenses.map((l) => l.state) : jurisdictions).filter(
      (s) => Boolean(s) && s !== 'federal'
    )
  ),
]

/**
 * The scripted "Building your report…" steps. Cosmetic (timed, not real backend
 * events) but truthful — they mirror the composer's real stages. Only the
 * jurisdiction is dynamic, taken from the just-submitted licenses.
 */
const joinStates = (names: string[]): string => {
  if (names.length <= 1) return names[0] ?? ''
  if (names.length === 2) return `${names[0]} and ${names[1]}`
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`
}

export const buildSteps = (jurisdictions: string[]): string[] => {
  const step1 =
    jurisdictions.length === 0
      ? 'Pulling your state filing calendar'
      : `Pulling the ${joinStates(jurisdictions)} filing ${
          jurisdictions.length > 1 ? 'calendars' : 'calendar'
        }`
  return [
    step1,
    'Matching your license renewal and CEU cycle',
    'Listing your quarterly tax dates',
    'Checking gaps against your practice stage',
    'Composing your recommendations',
  ]
}

/** The box a question sits in, from the moment it's asked through to its
 *  answered summary. Shared between the two so that answering one reads as
 *  the same box shrinking, rather than a swap between two different surfaces.
 *  Padding is left to each, since the summary row is tighter than the
 *  question it stands in for. */
export const INTAKE_QUESTION_CARD_CLS =
  'rounded-lg border border-neutral-100 bg-neutral-0'

/** Top of the income slider's scale. Exported because `formatThousands` and
 *  the slider's own `max` have to agree — split, raising the ceiling would
 *  silently stop the readout saying `+` at the top. */
export const INCOME_BAND_SLIDER_MAX = 200_000

/** `$130K`, or `$200K+` at the top of the scale. */
export const formatThousands = (amount: number): string =>
  amount >= INCOME_BAND_SLIDER_MAX
    ? `$${amount / 1000}K+`
    : `$${amount / 1000}K`

/**
 * Formik-only companion field holding the exact dollar amount behind an
 * `income_band_slider` answer.
 *
 * The answer itself has to stay one of the four band strings the backend
 * validates, which loses the number the user actually landed on — so the
 * amount rides alongside in Formik, where the collapsed summary can read it
 * back. Deliberately never mirrored into Redux: that keeps it out of the
 * submission (an unknown key is rejected at the request boundary), at the cost
 * of not surviving a reload. A returning session falls back to the band label.
 */
export const incomeAmountFieldName = (key: string): string => `${key}__amount`

/**
 * Credentials that carry prescribing scope, and so a DEA registration.
 *
 * Mirrors the `nurse_prescriber` family in the API's licence catalogue
 * (`apps/api/src/api/component/license/license_catalog.ts`) — the API sends
 * only `{ code, label }` per option, so the family can't be read at runtime.
 * If a prescribing credential is added there, add it here too.
 */
export const PRESCRIBER_LICENSE_CODES = ['PMHNP', 'APRN']
